import React from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Button, CloseButton, Flex, Group, Input, Paper } from '@mantine/core';

import styles from './TagsMenu.module.scss';

interface Props {
  isOpen: boolean;
  tags: {
    id: number;
    name: string;
    selected: boolean;
  }[];
  onSubmit: (editedTags: { id: number, name: string }[], deletedTags: number[]) => Promise<void>;
  onClose: () => void;
}

const EditTagsMenu = ({ isOpen, tags, onSubmit, onClose }: Props) => {
  const [editedTags, setEditedTags] = React.useState<{ [id: number]: string }>({});
  const [markedForDeletionTagIds, setMarkedForDeletionTagIds] = React.useState<number[]>([]);

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [submitError, setSubmitError] = React.useState(false);

  const handleTagInputChange = (id: number, value: string) => {
    setEditedTags((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleTagEditSubmit = async () => {
    const updatedTags = Object.entries(editedTags)
      .map(([id, name]) => ({ id: Number(id), name }))
      .filter(tag => markedForDeletionTagIds.indexOf(tag.id) === -1);

    if (updatedTags.length === 0 && markedForDeletionTagIds.length === 0) {
      onClose();
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError(false);

      await onSubmit(updatedTags, markedForDeletionTagIds);

      onClose();
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const markTagForDeletion = (id: number) => () => {
    setMarkedForDeletionTagIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter(tagId => tagId !== id);
      }
      return [...prev, id];
    });
  };

  React.useEffect(() => {
    if (!isOpen) {
      return;
    }

    // Set initial values
    const initialEditedTags: { [id: number]: string } = {};
    tags.forEach((tag) => {
      initialEditedTags[tag.id] = tag.name;
    });
    setEditedTags(initialEditedTags);
  }, [isOpen, tags]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div className={styles.overlay} />
      <Paper
        classNames={{
          root: styles.menu,
        }}
      >
        <Flex direction="column" gap={10} pr={10} mah={200} style={{ overflowY: 'auto' }}>
          {tags.map((tag) => (
            <Flex key={tag.id} justify="space-between" align="center" gap={10}>
              <Input
                value={editedTags[tag.id]}
                onChange={(event) => handleTagInputChange(tag.id, event.currentTarget.value)}
                classNames={{ input: styles.input }}
                style={{
                  flex: 1,
                  textDecoration: markedForDeletionTagIds.includes(tag.id) ? 'line-through' : 'none',
                  color: markedForDeletionTagIds.includes(tag.id) ? '#888' : 'inherit',
                  background: markedForDeletionTagIds.includes(tag.id) ? '#f5f5f5' : 'inherit',
                }}
                disabled={markedForDeletionTagIds.includes(tag.id)}
              />

              <Button
                variant="outline"
                size="xs"
                color="red"
                classNames={{ root: styles.deleteButton }}
                onClick={markTagForDeletion(tag.id)}
              >
                {markedForDeletionTagIds.includes(tag.id) ? 'UNDELETE' : 'DELETE'}
              </Button>
            </Flex>
          ))}
        </Flex>

        <Flex justify="center" align="center" gap={10} mt={30}>
          <Button
            variant="subtle"
            onClick={onClose}
            classNames={{ root: styles.cancelButton }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleTagEditSubmit}
            classNames={{ root: styles.submitButton }}
            disabled={isSubmitting}
          >
            Apply
          </Button>
        </Flex>
      </Paper>
    </>
  );
}

export default EditTagsMenu;
